/**
 * Catmull-Rom 1-Take Continuous 3D Camera Trajectory Controller
 * Provides 6-DOF camera splines with spring-damped parallax offset across all 10 scenes.
 */

export interface CameraState {
  px: number; py: number; pz: number; // Position
  tx: number; ty: number; tz: number; // Target LookAt
  fov: number;
}

export class CameraSpline {
  private currentSceneProgress: number = 0;
  private targetSceneProgress: number = 0;

  // Spring-damped parallax target & current
  private parallaxX = 0;
  private parallaxY = 0;
  private targetParallaxX = 0;
  private targetParallaxY = 0;

  // Keyframe Camera Points for Scenes 0 to 9
  private sceneWaypoints: CameraState[] = [
    { px: 0, py: 0, pz: 5, tx: 0, ty: 0, tz: 0, fov: 60 },      // Scene 01: Flint Cavern
    { px: 0, py: -2, pz: 6, tx: 0, ty: -2, tz: 0, fov: 55 },    // Scene 02: Monolith Pass
    { px: 0, py: -4, pz: 8, tx: 0, ty: -4, tz: 0, fov: 65 },    // Scene 03: Alignment Arch
    { px: 0, py: -6, pz: 7, tx: 0, ty: -6, tz: 0, fov: 60 },    // Scene 04: Collapse Bridge
    { px: 0, py: -8, pz: 5, tx: 0, ty: -8, tz: 0, fov: 50 },    // Scene 05: Anvil Forge
    { px: 0, py: -10, pz: 9, tx: 0, ty: -10, tz: 0, fov: 70 },  // Scene 06: Ocean Ring
    { px: 0, py: -12, pz: 4, tx: 0, ty: -12, tz: 0, fov: 45 },  // Scene 07: Clockwork Gears
    { px: 0, py: -14, pz: 6, tx: 0, ty: -14, tz: 0, fov: 60 },  // Scene 08: Dissolve Ring
    { px: 0, py: -16, pz: 8, tx: 0, ty: -16, tz: 0, fov: 75 },  // Scene 09: Cyber Storm
    { px: 0, py: -18, pz: 5, tx: 0, ty: -18, tz: 0, fov: 50 }   // Scene 10: Post-Singularity
  ];

  public setParallax(normX: number, normY: number): void {
    this.targetParallaxX = normX * 0.4;
    this.targetParallaxY = normY * 0.4;
  }

  public setTargetScene(sceneIndex: number): void {
    this.targetSceneProgress = Math.max(0, Math.min(9, sceneIndex));
  }

  public update(dt: number): CameraState {
    // Smooth Catmull-Rom lerp towards target scene
    this.currentSceneProgress += (this.targetSceneProgress - this.currentSceneProgress) * (dt * 3.0);

    // Spring damping for mouse parallax offset
    this.parallaxX += (this.targetParallaxX - this.parallaxX) * (dt * 5.0);
    this.parallaxY += (this.targetParallaxY - this.parallaxY) * (dt * 5.0);

    const idx = Math.floor(this.currentSceneProgress);
    const t = this.currentSceneProgress - idx;

    const p0 = this.sceneWaypoints[Math.max(0, idx - 1)];
    const p1 = this.sceneWaypoints[idx];
    const p2 = this.sceneWaypoints[Math.min(9, idx + 1)];
    const p3 = this.sceneWaypoints[Math.min(9, idx + 2)];

    const px = this.catmullRom(p0.px, p1.px, p2.px, p3.px, t) + this.parallaxX;
    const py = this.catmullRom(p0.py, p1.py, p2.py, p3.py, t) + this.parallaxY;
    const pz = this.catmullRom(p0.pz, p1.pz, p2.pz, p3.pz, t);

    const tx = this.catmullRom(p0.tx, p1.tx, p2.tx, p3.tx, t);
    const ty = this.catmullRom(p0.ty, p1.ty, p2.ty, p3.ty, t);
    const tz = this.catmullRom(p0.tz, p1.tz, p2.tz, p3.tz, t);

    const fov = this.catmullRom(p0.fov, p1.fov, p2.fov, p3.fov, t);

    return { px, py, pz, tx, ty, tz, fov };
  }

  private catmullRom(p0: number, p1: number, p2: number, p3: number, t: number): number {
    const v0 = (p2 - p0) * 0.5;
    const v1 = (p3 - p1) * 0.5;
    const t2 = t * t;
    const t3 = t2 * t;

    return (2 * p1 - 2 * p2 + v0 + v1) * t3 +
           (-3 * p1 + 3 * p2 - 2 * v0 - v1) * t2 +
           v0 * t + p1;
  }
}
